import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { RootState } from "integrations/redux/rootReducer"
export type { ComparisonClip } from "./layers/OrthoComparisonExtension"

export type ComparisonDirection = "left" | "right" | "up" | "down"

export type OrthoComparisonBounds = {
    minLng: number
    maxLng: number
    minLat: number
    maxLat: number
}

export function coordinatesToComparisonBounds(
    bbox: { latitude: number; longitude: number }[] | undefined
): OrthoComparisonBounds | null {
    if (!bbox || bbox.length === 0) return null
    let minLng = Infinity,
        maxLng = -Infinity,
        minLat = Infinity,
        maxLat = -Infinity
    for (const c of bbox) {
        if (c.longitude < minLng) minLng = c.longitude
        if (c.longitude > maxLng) maxLng = c.longitude
        if (c.latitude < minLat) minLat = c.latitude
        if (c.latitude > maxLat) maxLat = c.latitude
    }
    return { minLng, maxLng, minLat, maxLat }
}

type OrthoComparisonState = {
    flightId: string | null
    direction: ComparisonDirection
    threshold: number | null // longitude for left/right, latitude for up/down
    bounds: OrthoComparisonBounds | null
    comparisonOpacity: number // 1–100, independent from the layer's persistent opacity
}

const initialState: OrthoComparisonState = {
    flightId: null,
    direction: "left",
    threshold: null,
    bounds: null,
    comparisonOpacity: 100,
}

const orthoComparisonSlice = createSlice({
    name: "orthoComparison",
    initialState,
    reducers: {
        activateOrthoComparison: (
            state,
            action: PayloadAction<{
                flightId: string
                bounds: OrthoComparisonBounds
                threshold: number
            }>
        ) => {
            state.flightId = action.payload.flightId
            state.bounds = action.payload.bounds
            state.threshold = action.payload.threshold
            state.direction = "left"
            state.comparisonOpacity = 100
        },
        deactivateOrthoComparison: (state) => {
            state.flightId = null
            state.threshold = null
            state.bounds = null
            state.comparisonOpacity = 100
        },
        setOrthoComparisonOpacity: (state, action: PayloadAction<number>) => {
            state.comparisonOpacity = action.payload
        },
        setOrthoComparisonThreshold: (state, action: PayloadAction<number>) => {
            state.threshold = action.payload
        },
        setOrthoComparisonDirection: (state, action: PayloadAction<ComparisonDirection>) => {
            state.direction = action.payload
            // Reset threshold to center of bounds for the new axis
            if (state.bounds) {
                const { minLng, maxLng, minLat, maxLat } = state.bounds
                state.threshold =
                    action.payload === "left" || action.payload === "right"
                        ? (minLng + maxLng) / 2
                        : (minLat + maxLat) / 2
            } else {
                state.threshold = null
            }
        },
    },
})

export const {
    activateOrthoComparison,
    deactivateOrthoComparison,
    setOrthoComparisonThreshold,
    setOrthoComparisonDirection,
    setOrthoComparisonOpacity,
} = orthoComparisonSlice.actions

export const selectOrthoComparison = (state: RootState) => state.orthoComparison

export const selectIsOrthoComparisonActive = (flightId: string) => (state: RootState) =>
    state.orthoComparison.flightId === flightId

export function directionToClip(
    direction: ComparisonDirection,
    threshold: number
): { threshold: number; axis: 0 | 1; hideHigh: boolean } {
    switch (direction) {
        case "left":
            return { threshold, axis: 0, hideHigh: true }
        case "right":
            return { threshold, axis: 0, hideHigh: false }
        case "up":
            return { threshold, axis: 1, hideHigh: false }
        case "down":
            return { threshold, axis: 1, hideHigh: true }
    }
}

export const uiReducer = { orthoComparison: orthoComparisonSlice.reducer }
